<template>
  <component v-bind:is="layout" v-if="ready"></component>
  <template v-else>
    <div
      class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
    >
      <div class="max-w-md w-full space-y-8">
        <v-loader-animation
          :text="'text-black'"
          :loading-text-style="'text-black text-lg'"
          :loading-text="$t('common.loadingText')"
        />
      </div>
    </div>
  </template>
  <div id="tooltip" />
</template>
<script lang="ts">
import { defineComponent, ref } from 'vue'
import { useAuth } from '@/services/user/Auth0UserProvider'
import store from '@/services/store'
import mapLayout from '@/layouts/mapLayout.vue'
import axios from '@/services/api/providers/Connection'
import vLoaderAnimation from '@/components/template/v-loader-animation.vue'
import AppSyncClient from '@/services/query/providers/Connection'
import { useRouter } from 'vue-router'
import { IdToken } from '@auth0/auth0-spa-js'
import UserSession from '@/services/models/UserSession'

export default defineComponent({
  name: 'App',
  setup() {
    // You can import 'loading' here and show a global loader if that's more convenient
    const { initializeAuth } = useAuth()
    const ready = ref<boolean>(false)
    const { push } = useRouter()

    store.commit('clearAuth0Token')
    store.commit('clearUserSession')

    initializeAuth(
      {
        domain: process.env.VUE_APP_AUTH0_DOMAIN as string,
        client_id: process.env.VUE_APP_AUTH0_CLIENTID as string,
        audience: process.env.VUE_APP_AUTH0_AUDIENCE as string,
        useRefreshTokens: true,
      },
      async (appState) => {
        if (appState && appState.targetUrl) {
          await push(appState.targetUrl)
          // ready.value = true は UserSession 取得後に設定
        }
      },
      () => {
        // onNoRedirectCallback: リダイレクトなしの場合に実行される（必須パラメータ）
        // 現在は特に処理なし
      }
    ).then(() => {
      const auth0 = useAuth()
      if (auth0.isAuthenticated.value) {
        // tokenを取得
        auth0.getTokenSilently().then((token) => {
          // Bearer Tokenを設定
          axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
          // Vuex保存
          store.commit('setAuth0Token', token)

          // UserSession Tokenを設定
          UserSession.get()
            .then((response) => {
              // operation_officesが存在する場合は新しいストア(myOffice)に保存して選択画面へ
              if (
                response?.data?.operation_offices &&
                response.data.operation_offices.length > 0
              ) {
                store.commit('setSelectableOperationOffices', response?.data)
                push({ name: 'selectOperationOffice' }).then(
                  () => (ready.value = true)
                )
                return
              }

              if (response?.data) {
                // Vuex保存
                store.commit('setUserSession', response?.data)
              }
              // 成功時は常にページを表示
              ready.value = true
            })
            .catch((error) => {
              console.error('UserSession fetch error:', error)
              // UserSessionが取得できない場合は404ページへ遷移
              push({ name: 'not-found' }).then(() => (ready.value = true))
            })

          AppSyncClient.token = token
        })
        auth0.getIdTokenClaims().then((idTokenClaims: IdToken | undefined) => {
          const idToken = idTokenClaims?.__raw || ''
          axios.defaults.headers.common['X-Id-Token'] = idToken
        })
      } else {
        ready.value = true
      }
    })

    return {
      ready,
    }
  },
  computed: {
    layout() {
      if (this.$route.meta.layout) {
        return this.$route.meta.layout
      }

      return 'mapLayout'
    },
  },
  components: {
    mapLayout: mapLayout,
    vLoaderAnimation,
  },
})
</script>
<style></style>
